// ---------------------------------------------------------------------------
// Analyst module — Case Dossier page (investigation workspace)
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable, type Column } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { formatDate as i18nFormatDate, type SupportedLocale } from '@noxverify/i18n';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const EVENT_CATEGORY_COLORS: Record<string, string> = {
  system: 'border-s-hairline-strong',
  applicant: 'border-s-info',
  platform_member: 'border-s-info',
  tenant_member: 'border-s-primary-light',
};

// ---------------------------------------------------------------------------
// Tabs component
// ---------------------------------------------------------------------------

interface Tab {
  key: string;
  label: string;
  count?: number;
}

function Tabs({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: Tab[];
  activeTab: string;
  onChange: (key: string) => void;
}) {
  const { t } = useTranslation('admin');
  return (
    <div className="border-b border-hairline" role="tablist" aria-label={t('caseDossier.nav.sections')}>
      <nav className="flex -mb-px gap-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            onClick={() => onChange(tab.key)}
            className={`py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline-strong'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ms-2 text-xs bg-surface-sunken text-ink-muted rounded-sm px-2 py-0.5">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section loading/error wrappers
// ---------------------------------------------------------------------------

function SectionLoading() {
  const { t } = useTranslation('admin');
  return (
    <div className="py-6" role="status" aria-label={t('common.loading')}>
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-surface-sunken rounded w-1/3" />
        <div className="h-10 bg-surface-sunken rounded" />
        <div className="h-10 bg-surface-sunken rounded" />
        <div className="h-10 bg-surface-sunken rounded" />
      </div>
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

function SectionError({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('admin');
  return (
    <div
      className="bg-danger-tint border border-danger/30 rounded-sm p-4 text-danger my-4"
      role="alert"
    >
      <p className="text-sm font-medium">{t('errors.loadSectionFailed')}</p>
      <p className="text-sm mt-1">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 px-3 py-1.5 text-xs font-medium bg-danger-tint hover:bg-danger-tint text-danger rounded transition-colors"
        >
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

function SectionEmpty({ message }: { message: string }) {
  return (
    <div className="py-8 text-center text-ink-muted text-sm">{message}</div>
  );
}

// ---------------------------------------------------------------------------
// Helper: extract error message
// ---------------------------------------------------------------------------

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (
    typeof err === 'object' &&
    err !== null &&
    'message' in err
  ) {
    return String((err as { message: unknown }).message);
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Helper: format dates consistently (locale-aware)
// ---------------------------------------------------------------------------

function formatDate(iso: string | null, locale: SupportedLocale): string {
  if (!iso) return '--';
  return i18nFormatDate(iso, locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso: string | null, locale: SupportedLocale): string {
  if (!iso) return '--';
  return i18nFormatDate(iso, locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Helper: safe JSON fetch
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string, fallbackError: string): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...getDebugHeaders(),
  };
  const res = await fetch(url, { headers });
  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const msg =
      typeof json === 'object' &&
      json !== null &&
      'error' in json &&
      typeof (json as { error: { message?: string } }).error === 'object'
        ? ((json as { error: { message?: string } }).error?.message ??
          fallbackError)
        : fallbackError;
    throw new Error(msg);
  }
  return json as T;
}

// ---------------------------------------------------------------------------
// Hooks — per-section data fetching
// ---------------------------------------------------------------------------

function useCaseDetail(caseId: string) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin
      .getCase(caseId, { headers: getDebugHeaders() })
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadFailed'))),
      )
      .finally(() => setLoading(false));
  }, [caseId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

interface SectionResource<T> {
  data: T;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  reload: () => void;
}

function useEvidence(
  caseId: string,
  enabled = true,
): SectionResource<admin.EvidenceSummary[]> {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.EvidenceSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // TODO: Replace with api.admin.getCaseEvidence(caseId) once the typed client method is added.
    fetchJson<{ data: admin.EvidenceSummary[] }>(
      `${API_BASE}/v1/admin/cases/${caseId}/evidence`,
      t('errors.requestFailed'),
    )
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadEvidenceFailed'))),
      )
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [caseId, t]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { data, loading, loaded, error, reload: load };
}

function useScreening(
  caseId: string,
  enabled = true,
): SectionResource<admin.ScreeningRequestSummary[]> {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.ScreeningRequestSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // TODO: Replace with api.admin.getCaseScreening(caseId) once the typed client method is added.
    fetchJson<{ data: admin.ScreeningRequestSummary[] }>(
      `${API_BASE}/v1/admin/cases/${caseId}/screening-requests`,
      t('errors.requestFailed'),
    )
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadScreeningFailed'))),
      )
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [caseId, t]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { data, loading, loaded, error, reload: load };
}

function useTimeline(
  caseId: string,
  enabled = true,
): SectionResource<admin.AuditEventSummary[]> {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.AuditEventSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin
      .listAuditEvents({ caseId }, { headers: getDebugHeaders() })
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadTimelineFailed'))),
      )
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [caseId, t]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { data, loading, loaded, error, reload: load };
}

function useDecisions(
  caseId: string,
  enabled = true,
): SectionResource<admin.DecisionSummary[]> {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.DecisionSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // TODO: Replace with api.admin.getCaseDecisions(caseId) once the typed client method is added.
    fetchJson<{ data: admin.DecisionSummary[] }>(
      `${API_BASE}/v1/admin/cases/${caseId}/decisions`,
      t('errors.requestFailed'),
    )
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadDecisionsFailed'))),
      )
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [caseId, t]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { data, loading, loaded, error, reload: load };
}

function useNotes(
  caseId: string,
  enabled = true,
): SectionResource<admin.ReviewNoteSummary[]> {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<admin.ReviewNoteSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // TODO: Replace with api.admin.getCaseNotes(caseId) once the typed client method is added.
    fetchJson<{ data: admin.ReviewNoteSummary[] }>(
      `${API_BASE}/v1/admin/cases/${caseId}/notes`,
      t('errors.requestFailed'),
    )
      .then((res) => setData(res.data))
      .catch((err: unknown) =>
        setError(extractErrorMessage(err, t('caseDossier.errors.loadNotesFailed'))),
      )
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [caseId, t]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { data, loading, loaded, error, reload: load };
}

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

function OverviewTab({ caseDetail }: { caseDetail: admin.CaseDetail }) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const caseTypeLabels = t('cases.types', { returnObjects: true }) as Record<string, string>;
  const c = caseDetail;

  return (
    <div className="space-y-6">
      {/* Metadata grid */}
      <section aria-labelledby="case-metadata-heading">
        <h3
          id="case-metadata-heading"
          className="text-sm font-semibold text-ink mb-3"
        >
          {t('caseDossier.headings.metadata')}
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetadataField label={t('caseDossier.metadata.caseType')}>
            {caseTypeLabels[c.caseType] ?? c.caseType}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.status')}>
            <StatusBadge value={c.status} />
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.jurisdiction')}>{c.jurisdictionId}</MetadataField>
          <MetadataField label={t('caseDossier.metadata.created')}>{formatDate(c.createdAt, locale)}</MetadataField>
          <MetadataField label={t('caseDossier.metadata.submitted')}>
            {formatDate(c.submittedAt, locale)}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.decided')}>
            {formatDate(c.decidedAt, locale)}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.expires')}>
            {formatDate(c.expiresAt, locale)}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.workflowTemplate')}>
            {c.workflowTemplateId ?? '--'}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.policyPack')}>
            {c.policyPackId ?? '--'}
          </MetadataField>
        </dl>
      </section>

      {/* Subject info */}
      <section aria-labelledby="subject-info-heading">
        <h3
          id="subject-info-heading"
          className="text-sm font-semibold text-ink mb-3"
        >
          {t('caseDossier.headings.subjectInfo')}
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MetadataField label={t('caseDossier.metadata.createdBy')}>
            {c.createdBySubjectType}
            {c.createdBySubjectId ? ` (${c.createdBySubjectId})` : ''}
          </MetadataField>
          <MetadataField label={t('caseDossier.metadata.tenant')}>{c.tenantId}</MetadataField>
          <MetadataField label={t('caseDossier.metadata.project')}>{c.projectId}</MetadataField>
        </dl>
      </section>

      {/* Next step guidance */}
      <section aria-labelledby="next-step-heading">
        <h3
          id="next-step-heading"
          className="text-sm font-semibold text-ink mb-3"
        >
          {t('caseDossier.headings.nextSteps')}
        </h3>
        <NextStepGuidance status={c.status} />
      </section>
    </div>
  );
}

function MetadataField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-raised rounded-sm border border-hairline p-3">
      <dt className="text-xs font-medium text-ink-muted mb-1">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

function NextStepGuidance({ status }: { status: string }) {
  const { t } = useTranslation('admin');
  const guidanceMap = t('caseDossier.guidance', { returnObjects: true }) as Record<string, string>;
  const colorMap: Record<string, string> = {
    draft: 'bg-surface-sunken border-hairline text-ink',
    submitted: 'bg-primary-tint border-primary/30 text-primary',
    awaiting_documents: 'bg-warning-tint border-warning/30 text-warning',
    processing: 'bg-primary-tint border-primary/30 text-primary',
    awaiting_provider_result: 'bg-primary-tint border-primary/30 text-primary',
    manual_review: 'bg-warning-tint border-warning/30 text-warning',
    resubmission_requested: 'bg-warning-tint border-warning/30 text-warning',
    approved: 'bg-primary-tint border-primary/30 text-primary',
    approved_with_conditions: 'bg-primary-tint border-primary/30 text-primary',
    rejected: 'bg-danger-tint border-danger/30 text-danger',
    suspended: 'bg-warning-tint border-warning/30 text-warning',
    expired: 'bg-surface-sunken border-hairline text-ink',
  };

  const text = guidanceMap[status] ?? guidanceMap.unknown ?? '';
  const color = colorMap[status] ?? 'bg-surface-sunken border-hairline text-ink';

  return (
    <div className={`rounded-sm border p-3 text-sm ${color}`}>{text}</div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Evidence
// ---------------------------------------------------------------------------

function buildEvidenceColumns(
  locale: SupportedLocale,
  t: ReturnType<typeof useTranslation>['t'],
): Column<admin.EvidenceSummary>[] {
  const evidenceTypeLabels = t('caseDossier.evidence.types', { returnObjects: true }) as Record<string, string>;
  return [
    {
      key: 'evidenceType',
      header: t('caseDossier.evidence.columns.type'),
      render: (e) => (
        <span className="text-sm text-ink">
          {evidenceTypeLabels[e.evidenceType] ?? e.evidenceType}
        </span>
      ),
    },
    {
      key: 'fileName',
      header: t('caseDossier.evidence.columns.fileName'),
      render: (e) => (
        <span className="text-sm font-mono text-ink">
          {e.fileName ?? '--'}
        </span>
      ),
    },
    {
      key: 'mimeType',
      header: t('caseDossier.evidence.columns.mimeType'),
      render: (e) => (
        <span className="text-sm text-ink-muted">{e.mimeType}</span>
      ),
    },
    {
      key: 'status',
      header: t('caseDossier.evidence.columns.status'),
      render: (e) => <StatusBadge value={e.status} />,
    },
    {
      key: 'fileSizeBytes',
      header: t('caseDossier.evidence.columns.size'),
      render: (e) => (
        <span className="text-sm text-ink-muted">
          {e.fileSizeBytes !== null
            ? `${(e.fileSizeBytes / 1024).toFixed(1)} KB`
            : '--'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: t('caseDossier.evidence.columns.uploaded'),
      render: (e) => (
        <span className="text-sm text-ink-muted">{formatDate(e.createdAt, locale)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: () => (
        <button
          type="button"
          disabled
          className="px-3 py-1 text-xs font-medium bg-surface-sunken text-ink-muted rounded cursor-not-allowed"
          title={t('caseDossier.evidence.actions.viewTooltip')}
        >
          {t('caseDossier.evidence.actions.view')}
        </button>
      ),
    },
  ];
}

function EvidenceTab({
  resource,
}: {
  resource: SectionResource<admin.EvidenceSummary[]>;
}) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const { data, loading, loaded, error, reload } = resource;
  const evidenceColumns = React.useMemo(() => buildEvidenceColumns(locale, t), [locale, t]);

  if (loading || !loaded) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;
  if (data.length === 0) return <SectionEmpty message={t('caseDossier.evidence.empty')} />;

  return (
    <DataTable
      columns={evidenceColumns}
      data={data}
      rowKey={(e) => e.id}
      emptyMessage={t('caseDossier.evidence.empty')}
      ariaLabel={t('caseDossier.evidence.ariaLabel')}
    />
  );
}

// ---------------------------------------------------------------------------
// Tab: Screening
// ---------------------------------------------------------------------------

function buildScreeningColumns(
  locale: SupportedLocale,
  t: ReturnType<typeof useTranslation>['t'],
): Column<admin.ScreeningRequestSummary>[] {
  return [
    {
      key: 'screeningEngine',
      header: t('caseDossier.screening.columns.engine'),
      render: (s) => (
        <span className="text-sm text-ink">{s.screeningEngine}</span>
      ),
    },
    {
      key: 'subjectType',
      header: t('caseDossier.screening.columns.subject'),
      render: (s) => (
        <span className="text-sm text-ink-muted">{s.subjectType}</span>
      ),
    },
    {
      key: 'status',
      header: t('caseDossier.screening.columns.status'),
      render: (s) => <StatusBadge value={s.status} />,
    },
    {
      key: 'matchCount',
      header: t('caseDossier.screening.columns.matches'),
      render: (s) => (
        <span className="text-sm text-ink font-medium">{s.matchCount}</span>
      ),
    },
    {
      key: 'hitCount',
      header: t('caseDossier.screening.columns.hits'),
      render: (s) => (
        <span
          className={`text-sm font-medium ${
            s.hitCount > 0 ? 'text-danger' : 'text-ink-muted'
          }`}
        >
          {s.hitCount}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: t('caseDossier.screening.columns.created'),
      render: (s) => (
        <span className="text-sm text-ink-muted">{formatDate(s.createdAt, locale)}</span>
      ),
    },
    {
      key: 'completedAt',
      header: t('caseDossier.screening.columns.completed'),
      render: (s) => (
        <span className="text-sm text-ink-muted">
          {formatDate(s.completedAt, locale)}
        </span>
      ),
    },
  ];
}

function ScreeningTab({
  resource,
}: {
  resource: SectionResource<admin.ScreeningRequestSummary[]>;
}) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const { data, loading, loaded, error, reload } = resource;
  const screeningColumns = React.useMemo(() => buildScreeningColumns(locale, t), [locale, t]);

  if (loading || !loaded) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;
  if (data.length === 0)
    return <SectionEmpty message={t('caseDossier.screening.empty')} />;

  return (
    <DataTable
      columns={screeningColumns}
      data={data}
      rowKey={(s) => s.id}
      emptyMessage={t('caseDossier.screening.empty')}
      ariaLabel={t('caseDossier.screening.ariaLabel')}
    />
  );
}

// ---------------------------------------------------------------------------
// Tab: Timeline
// ---------------------------------------------------------------------------

function TimelineTab({
  resource,
}: {
  resource: SectionResource<admin.AuditEventSummary[]>;
}) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const { data, loading, loaded, error, reload } = resource;

  if (loading || !loaded) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;
  if (data.length === 0)
    return <SectionEmpty message={t('caseDossier.timeline.empty')} />;

  // Sort by createdAt descending (most recent first)
  const sorted = [...data].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="space-y-0" role="list" aria-label={t('caseDossier.timeline.ariaLabel')}>
      {sorted.map((event) => {
        const borderColor =
          EVENT_CATEGORY_COLORS[event.actorType] ?? 'border-s-hairline';
        return (
          <div
            key={event.id}
            role="listitem"
            className={`border-s-4 ${borderColor} ps-4 py-3 flex flex-col sm:flex-row sm:items-start gap-2`}
          >
            <div className="flex-shrink-0 w-40">
              <span className="text-xs text-ink-muted">
                {formatDateTime(event.createdAt, locale)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-ink">
                  {event.eventType}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-sm bg-surface-sunken text-ink-muted">
                  {event.actorType}
                </span>
              </div>
              <p className="text-sm text-ink-muted mt-0.5">
                {t('caseDossier.timeline.actionLine', { action: event.action, resourceType: event.resourceType })}{' '}
                <span className="font-mono text-xs text-ink-muted">
                  {event.resourceId}
                </span>
              </p>
              {event.actorId && (
                <p className="text-xs text-ink-muted mt-0.5">
                  {t('caseDossier.timeline.actor', { actorId: event.actorId })}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Decisions
// ---------------------------------------------------------------------------

function DecisionsTab({
  resource,
}: {
  resource: SectionResource<admin.DecisionSummary[]>;
}) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const { data, loading, loaded, error, reload } = resource;

  if (loading || !loaded) return <SectionLoading />;
  if (error) return <SectionError message={error} onRetry={reload} />;
  if (data.length === 0)
    return <SectionEmpty message={t('caseDossier.decisions.empty')} />;

  return (
    <div className="space-y-4" role="list" aria-label={t('caseDossier.decisions.ariaLabel')}>
      {data.map((decision) => (
        <div
          key={decision.id}
          role="listitem"
          className="bg-surface-raised rounded-sm border border-hairline p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <DecisionTypeBadge type={decision.decisionType} />
              {decision.requiresDualControl && (
                <span className="text-xs px-2 py-0.5 rounded-sm bg-warning-tint text-warning">
                  {t('caseDossier.decisions.dualControl')}
                </span>
              )}
            </div>
            <span className="text-xs text-ink-muted">
              {formatDateTime(decision.createdAt, locale)}
            </span>
          </div>
          <p className="text-sm text-ink-muted mb-2">
            {decision.decidedBySubjectId
              ? t('caseDossier.decisions.byActorWithId', {
                  actorType: decision.decidedBySubjectType,
                  actorId: decision.decidedBySubjectId,
                })
              : t('caseDossier.decisions.byActor', { actorType: decision.decidedBySubjectType })}
          </p>
          {decision.reasons.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-ink-muted uppercase tracking-wider">
                {t('caseDossier.headings.reasons')}
              </p>
              {decision.reasons.map((reason, idx) => (
                <div
                  key={idx}
                  className="text-sm bg-surface-sunken rounded px-3 py-2"
                >
                  <span className="font-mono text-xs text-ink-muted me-2">
                    {reason.reasonCode}
                  </span>
                  {reason.reasonText && (
                    <span className="text-ink">{reason.reasonText}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DecisionTypeBadge({ type }: { type: string }) {
  const { t } = useTranslation('admin');
  const decisionLabels = t('caseDossier.decisions.types', { returnObjects: true }) as Record<string, string>;
  const colorMap: Record<string, string> = {
    approve: 'bg-primary-tint text-primary',
    approve_with_conditions: 'bg-primary-tint text-primary',
    reject: 'bg-danger-tint text-danger',
    request_resubmission: 'bg-warning-tint text-warning',
    escalate: 'bg-warning-tint text-warning',
    suspend: 'bg-warning-tint text-warning',
    override: 'bg-info-tint text-info',
  };

  const color = colorMap[type] ?? 'bg-surface-sunken text-ink';
  const label = decisionLabels[type] ?? type.replace(/_/g, ' ');

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab: Notes
// ---------------------------------------------------------------------------

function NotesTab({
  caseId,
  resource,
}: {
  caseId: string;
  resource: SectionResource<admin.ReviewNoteSummary[]>;
}) {
  const { t } = useTranslation('admin');
  const { locale } = useFormatters();
  const { data, loading, loaded, error, reload } = resource;

  return (
    <div>
      {/* Existing notes list */}
      {(loading || !loaded) && <SectionLoading />}
      {!loading && loaded && error && (
        <SectionError message={error} onRetry={reload} />
      )}
      {!loading && loaded && !error && data.length === 0 && (
        <SectionEmpty message={t('caseDossier.notes.empty')} />
      )}
      {!loading && loaded && !error && data.length > 0 && (
        <div className="space-y-3" role="list" aria-label={t('caseDossier.notes.ariaLabel')}>
          {data.map((note) => (
            <div
              key={note.id}
              role="listitem"
              className="bg-surface-raised rounded-sm border border-hairline p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink">
                    {note.authorId
                      ? t('caseDossier.notes.byActorWithId', {
                          actorType: note.authorType,
                          actorId: note.authorId,
                        })
                      : t('caseDossier.notes.byActor', { actorType: note.authorType })}
                  </span>
                  <VisibilityBadge visibility={note.visibility} />
                </div>
                <span className="text-xs text-ink-muted">
                  {formatDateTime(note.createdAt, locale)}
                </span>
              </div>
              <p className="text-sm text-ink whitespace-pre-wrap">
                {note.content}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Add note form */}
      <AddNoteForm caseId={caseId} onNoteAdded={reload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Note form
// ---------------------------------------------------------------------------

function AddNoteForm({
  caseId,
  onNoteAdded,
}: {
  caseId: string;
  onNoteAdded: () => void;
}) {
  const { t } = useTranslation('admin');
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<admin.ReviewNoteVisibility>('internal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    setError('');
    try {
      // TODO: Replace with api.admin.createNote(caseId, ...) once the typed client method is added.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getDebugHeaders(),
      };
      const res = await fetch(`${API_BASE}/v1/admin/cases/${caseId}/notes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: content.trim(),
          visibility,
        } satisfies admin.CreateReviewNoteRequest),
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const msg =
          typeof json === 'object' &&
          json !== null &&
          'error' in json &&
          typeof (json as { error: { message?: string } }).error === 'object'
            ? ((json as { error: { message?: string } }).error?.message ?? t('errors.requestFailed'))
            : t('errors.requestFailed');
        throw new Error(msg);
      }
      setContent('');
      onNoteAdded();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t('caseDossier.messages.addNoteFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 border-t border-hairline pt-6">
      <h4 className="text-sm font-semibold text-ink mb-3">{t('caseDossier.headings.addNote')}</h4>
      <label htmlFor="note-content" className="sr-only">
        {t('caseDossier.notes.contentLabel')}
      </label>
      <textarea
        id="note-content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('caseDossier.notes.placeholder')}
        rows={3}
        className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        disabled={submitting}
        aria-describedby={error ? 'note-error' : undefined}
      />
      <div className="flex items-center justify-between mt-3">
        <label htmlFor="note-visibility" className="flex items-center gap-2 text-sm text-ink-muted">
          <span className="sr-only">{t('caseDossier.notes.visibilityLabel')}</span>
          <select
            id="note-visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as admin.ReviewNoteVisibility)}
            className="border border-hairline-strong rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            disabled={submitting}
          >
            <option value="internal">{t('caseDossier.notes.internalOption')}</option>
            <option value="applicant_visible">{t('caseDossier.notes.applicantVisibleOption')}</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={submitting || !content.trim()}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? t('caseDossier.notes.submitting') : t('caseDossier.notes.submit')}
        </button>
      </div>
      {error && (
        <p id="note-error" className="text-sm text-danger mt-2" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

function VisibilityBadge({
  visibility,
}: {
  visibility: admin.ReviewNoteVisibility;
}) {
  const { t } = useTranslation('admin');
  const isInternal = visibility === 'internal';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${
        isInternal
          ? 'bg-surface-sunken text-ink'
          : 'bg-primary-tint text-primary'
      }`}
    >
      {isInternal ? t('caseDossier.notes.internal') : t('caseDossier.notes.applicantVisible')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Action buttons (header)
// ---------------------------------------------------------------------------

const DECISION_VARIANT_MAP: Record<string, 'danger' | 'warning'> = {
  approve: 'warning',
  reject: 'danger',
  request_resubmission: 'warning',
  escalate: 'warning',
  suspend: 'danger',
};

function buildDecisionIdempotencyKey(caseId: string, decisionType: admin.DecisionType): string {
  return `decision_${caseId}_${decisionType}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function ActionButtons({
  caseId,
  status,
  onActionComplete,
}: {
  caseId: string;
  status: string;
  onActionComplete: () => void;
}) {
  const { t } = useTranslation('admin');
  const isTerminal = ['approved', 'approved_with_conditions', 'rejected', 'expired'].includes(status);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    decisionType: admin.DecisionType;
    idempotencyKey: string;
  } | null>(null);

  const actionButtons: Array<{
    decisionType: admin.DecisionType;
    label: string;
    ariaLabel: string;
    colorClasses: string;
    ringColor: string;
  }> = [
    {
      decisionType: 'approve',
      label: t('caseDossier.actions.approve'),
      ariaLabel: t('caseDossier.actions.approveAria', { caseId }),
      colorClasses: 'bg-primary hover:bg-primary-hover',
      ringColor: 'focus-visible:ring-primary',
    },
    {
      decisionType: 'reject',
      label: t('caseDossier.actions.reject'),
      ariaLabel: t('caseDossier.actions.rejectAria', { caseId }),
      colorClasses: 'bg-danger hover:bg-danger',
      ringColor: 'focus-visible:ring-danger',
    },
    {
      decisionType: 'request_resubmission',
      label: t('caseDossier.actions.requestResubmission'),
      ariaLabel: t('caseDossier.actions.requestResubmissionAria', { caseId }),
      colorClasses: 'bg-warning hover:bg-warning',
      ringColor: 'focus-visible:ring-warning',
    },
    {
      decisionType: 'escalate',
      label: t('caseDossier.actions.escalate'),
      ariaLabel: t('caseDossier.actions.escalateAria', { caseId }),
      colorClasses: 'bg-warning hover:bg-warning',
      ringColor: 'focus-visible:ring-warning',
    },
  ];

  const executeAction = async (decisionType: admin.DecisionType, idempotencyKey: string) => {
    const friendlyName = decisionType.replace(/_/g, ' ');

    setActionLoading(decisionType);
    setActionError(null);
    try {
      await api.admin.createDecision(
        caseId,
        {
          decisionType,
          reasons: [{ reasonCode: decisionType, reasonText: `${friendlyName} by operator` }],
        },
        { headers: getDebugHeaders(), idempotencyKey },
      );
      onActionComplete();
    } catch (err: unknown) {
      setActionError(
        t('caseDossier.actions.failedAction', {
          action: friendlyName,
          message: extractErrorMessage(err, t('errors.unknown')),
        }),
      );
    } finally {
      setActionLoading(null);
    }
  };

  const pendingFriendlyName = pendingAction ? pendingAction.decisionType.replace(/_/g, ' ') : '';

  return (
    <div className="flex flex-col items-end gap-2">
      {actionError && (
        <div
          className="flex items-center gap-2 bg-danger-tint border border-danger/30 rounded-sm px-3 py-2 text-sm text-danger w-full sm:w-auto"
          role="alert"
        >
          <span className="flex-1">{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="text-danger hover:text-danger font-medium text-xs flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger rounded"
            aria-label={t('caseDossier.actions.dismissErrorAria')}
          >
            {t('caseDossier.actions.dismissError')}
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {actionButtons.map((btn) => {
          const isLoading = actionLoading === btn.decisionType;
          const isDisabled = isTerminal || actionLoading !== null;
          return (
            <button
              key={btn.decisionType}
              type="button"
              disabled={isDisabled}
              onClick={() => setPendingAction({
                decisionType: btn.decisionType,
                idempotencyKey: buildDecisionIdempotencyKey(caseId, btn.decisionType),
              })}
              className={`px-4 py-2 text-sm font-medium text-white rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${btn.colorClasses} ${btn.ringColor}`}
              aria-label={btn.ariaLabel}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-1.5">
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {btn.label}...
                </span>
              ) : (
                btn.label
              )}
            </button>
          );
        })}
      </div>

      <ConfirmDialog
        open={pendingAction !== null}
        title={t('caseDossier.actions.confirmTitle', { action: pendingFriendlyName })}
        message={t('caseDossier.actions.confirmMessage', { action: pendingFriendlyName })}
        confirmLabel={pendingFriendlyName.charAt(0).toUpperCase() + pendingFriendlyName.slice(1)}
        variant={pendingAction ? (DECISION_VARIANT_MAP[pendingAction.decisionType] ?? 'danger') : 'danger'}
        onConfirm={() => {
          if (pendingAction) {
            executeAction(pendingAction.decisionType, pendingAction.idempotencyKey);
          }
          setPendingAction(null);
        }}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function CaseDossierPage() {
  const { t } = useTranslation('admin');
  const { caseId } = useParams<{ caseId: string }>();
  const [activeTab, setActiveTab] = useState('overview');

  // Load only the primary case detail on mount; tab resources load on demand.
  const caseDetail = useCaseDetail(caseId ?? '');
  const evidence = useEvidence(caseId ?? '', activeTab === 'evidence' && Boolean(caseId));
  const screening = useScreening(caseId ?? '', activeTab === 'screening' && Boolean(caseId));
  const timeline = useTimeline(caseId ?? '', activeTab === 'timeline' && Boolean(caseId));
  const decisions = useDecisions(caseId ?? '', activeTab === 'decisions' && Boolean(caseId));
  const notes = useNotes(caseId ?? '', activeTab === 'notes' && Boolean(caseId));

  /**
   * Refresh visible case data after a mutating action (decision, note, etc.).
   * Inactive tab resources intentionally stay lazy and load current data when opened.
   */
  const refreshCase = useCallback(() => {
    caseDetail.reload();
    if (activeTab === 'decisions') decisions.reload();
    if (activeTab === 'timeline') timeline.reload();
    if (activeTab === 'notes') notes.reload();
  }, [
    activeTab,
    caseDetail.reload,
    decisions.reload,
    timeline.reload,
    notes.reload,
  ]);

  if (!caseId) {
    return (
      <div className="p-6">
        <ErrorState message={t('caseDossier.messages.noCaseId')} />
      </div>
    );
  }

  // Full-page loading only for the primary case detail
  if (caseDetail.loading) return <LoadingState rows={6} />;
  if (caseDetail.error)
    return (
      <ErrorState message={caseDetail.error} onRetry={caseDetail.reload} />
    );
  if (!caseDetail.data)
    return <ErrorState message={t('caseDossier.messages.notFound')} />;

  const c = caseDetail.data;
  const caseTypeLabels = t('cases.types', { returnObjects: true }) as Record<string, string>;

  const tabs: Tab[] = [
    { key: 'overview', label: t('caseDossier.tabs.overview') },
    {
      key: 'evidence',
      label: t('caseDossier.tabs.evidence'),
      count: evidence.loaded && !evidence.loading ? evidence.data.length : undefined,
    },
    {
      key: 'screening',
      label: t('caseDossier.tabs.screening'),
      count: screening.loaded && !screening.loading ? screening.data.length : undefined,
    },
    {
      key: 'timeline',
      label: t('caseDossier.tabs.timeline'),
      count: timeline.loaded && !timeline.loading ? timeline.data.length : undefined,
    },
    {
      key: 'decisions',
      label: t('caseDossier.tabs.decisions'),
      count: decisions.loaded && !decisions.loading ? decisions.data.length : undefined,
    },
    {
      key: 'notes',
      label: t('caseDossier.tabs.notes'),
      count: notes.loaded && !notes.loading ? notes.data.length : undefined,
    },
  ];

  return (
    <div className="p-6 max-w-7xl">
      {/* Back link */}
      <nav aria-label={t('caseDossier.nav.breadcrumb')} className="mb-4">
        <Link
          to="/tenant/cases"
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 12L6 8l4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t('caseDossier.nav.backToCases')}
        </Link>
      </nav>

      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-display text-3xl text-ink">
                {t('caseDossier.headings.title')}
              </h1>
              <StatusBadge value={c.status} />
            </div>
            <div className="flex items-center gap-3 text-sm text-ink-muted">
              <span className="font-mono">{c.id}</span>
              <span className="text-ink-muted">|</span>
              <span>{caseTypeLabels[c.caseType] ?? c.caseType}</span>
              <span className="text-ink-muted">|</span>
              <span>{c.jurisdictionId}</span>
            </div>
          </div>
          <ActionButtons caseId={c.id} status={c.status} onActionComplete={refreshCase} />
        </div>
      </header>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab panels */}
      <div className="mt-6">
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'overview' && <OverviewTab caseDetail={c} />}
          {activeTab === 'evidence' && <EvidenceTab resource={evidence} />}
          {activeTab === 'screening' && <ScreeningTab resource={screening} />}
          {activeTab === 'timeline' && <TimelineTab resource={timeline} />}
          {activeTab === 'decisions' && <DecisionsTab resource={decisions} />}
          {activeTab === 'notes' && (
            <NotesTab caseId={caseId} resource={notes} />
          )}
        </div>
      </div>
    </div>
  );
}
